/**
 * Field visibility rules — show/hide a form field based on the values of other fields.
 *
 * Generic registry keyed by Aras ItemType name. Each rule declares the fields it watches
 * (`dependsOn`) and a predicate `isVisible(values)` returning true to render the field.
 *
 * Add a new rule by inserting an entry under the corresponding ItemType. No changes needed
 * in `DynamicFormSection` — it consumes this registry automatically.
 *
 * Migration note: long-term these rules should come from Aras metadata (Task 31 family) so
 * non-developers can author them. The registry exists as the minimal-surface bridge until
 * that pipeline is in place.
 */
export interface FieldVisibilityRule {
  /** Names of form fields this rule reads. Used to set up Form.useWatch subscriptions. */
  dependsOn: string[];
  /** Returns true when the field should be rendered. */
  isVisible: (values: Record<string, unknown>) => boolean;
  /**
   * Optional sanity hint: list-property values referenced by `isVisible`, keyed by the
   * dependsOn field name. DynamicFormSection emits a `console.warn` if the loaded Aras
   * list options don't contain these expected values — flags drift between this registry
   * and the Aras List source-of-truth (e.g., admin renames a list value).
   */
  expectedListValues?: Record<string, string[]>;
}

export type FieldVisibilityMap = Record<string, FieldVisibilityRule>;

/**
 * Aras `List_OrgType` value referenced by Facility rules. Source-of-truth lives in the
 * Aras List ItemType `List_OrgType` (see `documents/AML/MissingFields/11_Facility.aml`).
 * The runtime warning in DynamicFormSection catches drift if Aras data changes.
 *
 * Why a literal: `List_OrgType` currently uses the Vietnamese label as the value column
 * (no separate code/key). Long-term: add a stable code column to the list and branch on it.
 */
const ORG_TYPE_BUSINESS = "Doanh nghiệp";

// Aras snake_case property names (`org_type`, `facility_id`, `organization_id`) are
// mapped to camelCase by the metadata pipeline before reaching DynamicForm — rules
// must use the camelCase `mappedName` so the predicate can read form values.
//
// Shared between any ItemType whose form has the org_type / facility_id / organization_id
// trio (Facility, Person, …). Branch on org_type: hide facility_id when business is
// selected; hide organization_id otherwise.
const ORG_TYPE_BRANCH_RULES: FieldVisibilityMap = {
  facilityId: {
    dependsOn: ["orgType"],
    isVisible: (values) => values.orgType !== ORG_TYPE_BUSINESS,
    expectedListValues: { orgType: [ORG_TYPE_BUSINESS] },
  },
  organizationId: {
    dependsOn: ["orgType"],
    isVisible: (values) => values.orgType === ORG_TYPE_BUSINESS,
    expectedListValues: { orgType: [ORG_TYPE_BUSINESS] },
  },
};

const RULES_BY_ITEM_TYPE: Record<string, FieldVisibilityMap> = {
  Facility: ORG_TYPE_BRANCH_RULES,
  Person: ORG_TYPE_BRANCH_RULES,
  Customer: ORG_TYPE_BRANCH_RULES,
};

const EMPTY_RULES: FieldVisibilityMap = Object.freeze({}) as FieldVisibilityMap;

export const getFieldVisibilityRules = (
  itemType: string | undefined,
): FieldVisibilityMap => {
  if (!itemType) return EMPTY_RULES;
  return RULES_BY_ITEM_TYPE[itemType] ?? EMPTY_RULES;
};
